import React from 'react'
import { Loading, useGetBusiness } from '@marketing-milk/frontend'
import { SprintDTO } from '@marketing-milk/interfaces'
import { Autocomplete, Button, Drawer, IconButton, Tab, Tabs, TextField } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import { format, fromUnixTime } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { ConfirmationModal } from 'components/ConfirmationModal'
import { FullPageContent } from 'components/layout/FullPageLayout/Content/FullPageContent'
import { ListPageBar } from 'components/layout/PageBar'
import { UserLayout } from 'components/layout/UserLayout'
import { NoItemsCreated } from 'components/NoItemsCreated/NoItemsCreated'
import { useNest } from 'hooks/api/useNest'
import { useSprint } from 'hooks/api/useSprint'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { CreateSprintForm } from './components/create-sprint-form/CreateSprintForm'
import { SprintSettingsStep } from './components/create-sprint-form/Steps/SprintSettingsStep'
import { SprintHelpIcon } from './components/sprint-help-icon/SprintHelpIcon'
import { SprintContent, SprintTab } from './components/sprints-content/SprintContent'
import { getDefaultSprintDto } from './sprint.helpers'

const tabs: { id: SprintTab; label: string }[] = [
  { id: 'creatives', label: 'Creatives' },
  { id: 'draft-campaigns', label: 'Draft Campaigns' },
  { id: 'published-campaigns', label: 'Published Campaigns' },
]

export function Sprints() {
  const location = useLocation()
  const history = useHistory()
  const sprintIdParam = new URLSearchParams(location.search).get('sprintId')
  const selectedSprintId = sprintIdParam ? Number(sprintIdParam) : undefined
  const [createSprintsModalOpen, setCreateSprintModalOpen] = useState(false)
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)
  const [editing, setEditing] = useState(false)

  const { list: sprints, create: createSprint, isLoading: sprintsLoading } = useNest('sprints')
  const { sprint, updateSprint, deleteSprint } = useSprint(selectedSprintId)
  const business = useGetBusiness()

  const [sprintDto, setSprintDto] = useState<SprintDTO>(getDefaultSprintDto(business.timezone))

  const requestedTab = new URLSearchParams(location.search).get('tab')
  const tab = tabs.find(({ id }) => id === requestedTab)?.id ?? 'creatives'
  const sortedSprints = useMemo(
    () => [...(sprints ?? [])].sort((a, b) => b.createdTime - a.createdTime),
    [sprints]
  )
  const selectedSprint = sortedSprints.find(({ id }) => id === selectedSprintId) ?? null
  const formatSprintDate = (time: number) =>
    format(utcToZonedTime(fromUnixTime(time), business.timezone), 'MMM d, yyyy')

  // Select the newest sprint on entry, or when the selected sprint was deleted.
  useEffect(() => {
    if (!sprints || selectedSprint) return
    const params = new URLSearchParams(location.search)
    if (sortedSprints[0]) params.set('sprintId', String(sortedSprints[0].id))
    else params.delete('sprintId')
    if (params.toString() !== new URLSearchParams(location.search).toString()) {
      history.replace(`${location.pathname}?${params.toString()}`)
    }
  }, [sprints, sortedSprints, selectedSprint, location.search, location.pathname, history])

  useEffect(() => {
    if (!sprint) return
    setSprintDto({
      name: sprint.name,
      budget: sprint.budget,
      endTime: sprint.endTime,
      startTime: sprint.startTime,
    })
  }, [sprint])

  const changeTab = (newTab: SprintTab) => {
    const params = new URLSearchParams()
    if (selectedSprintId) params.append('sprintId', selectedSprintId.toString())
    if (newTab !== 'creatives') params.append('tab', newTab)
    history.replace(`${location.pathname}?${params.toString()}`)
  }

  const startNewSprint = (
    <Button color="primary" onClick={() => setCreateSprintModalOpen(true)} variant="contained">
      Start New Sprint
    </Button>
  )

  const hasSprints = !!sprints && sprints.length > 0

  return (
    <UserLayout>
      <ListPageBar
        subject="Sprints"
        actions={
          <>
            {hasSprints && (
              <Autocomplete
                size="small"
                disableClearable
                options={sortedSprints}
                value={selectedSprint ?? sortedSprints[0]}
                getOptionLabel={option => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No matching sprints"
                onChange={(_, nextSprint) => {
                  if (!nextSprint) return
                  const params = new URLSearchParams(location.search)
                  params.set('sprintId', String(nextSprint.id))
                  params.delete('campaignId')
                  history.replace(`${location.pathname}?${params.toString()}`)
                }}
                sx={{ width: 340, maxWidth: '70vw' }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Find a sprint"
                    // This unlabeled field needs no legend; legacy global legend styles
                    // otherwise leave a gap across the top of MUI's outline.
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { top: 0 },
                      '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                    }}
                    inputProps={{ ...params.inputProps, 'aria-label': 'Select sprint' }}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <div className="min-w-0 flex-1 py-1">
                      <p
                        className={`break-words text-sm font-semibold ${
                          selected ? 'text-mm-primary-700' : ''
                        }`}
                      >
                        {option.name}
                      </p>
                      <p className="mt-0.5 text-xs text-mm-neutral-1-600">
                        {formatSprintDate(option.startTime)} – {formatSprintDate(option.endTime)}
                      </p>
                    </div>
                    {selected && <CheckIcon fontSize="small" color="primary" className="ml-2" />}
                  </li>
                )}
              />
            )}
            {startNewSprint}
            {hasSprints && (
              <>
                <Button
                  variant="text"
                  disabled={!sprint}
                  onClick={() => {
                    if (!sprint) return
                    setSprintDto({
                      name: sprint.name,
                      budget: sprint.budget,
                      endTime: sprint.endTime,
                      startTime: sprint.startTime,
                    })
                    setEditing(true)
                  }}
                >
                  Edit sprint
                </Button>
                <Button
                  variant="text"
                  color="error"
                  disabled={!sprint}
                  data-testid="sprint-delete-button"
                  onClick={() => setIsConfirmDeleteOpen(true)}
                >
                  Delete sprint
                </Button>
              </>
            )}
            <SprintHelpIcon />
          </>
        }
      />

      <Drawer
        anchor="right"
        open={createSprintsModalOpen}
        onClose={() => setCreateSprintModalOpen(false)}
      >
        <CreateSprintForm onSubmit={createSprint} onClose={() => setCreateSprintModalOpen(false)} />
      </Drawer>

      <Drawer anchor="right" open={editing} onClose={() => setEditing(false)}>
        {/* Four fields and a calendar — sized to the form rather than to the viewport. */}
        <div
          style={{ width: 'min(880px, 95vw)' }}
          className="flex h-full max-w-full flex-col overflow-hidden"
        >
          <div className="flex items-center gap-3 border-b border-mm-neutral-1-200 bg-white px-6 py-2">
            <h2 className="min-w-0 flex-1 truncate text-base font-bold text-mm-neutral-1-900">
              {sprint?.name}
            </h2>
            <IconButton onClick={() => setEditing(false)} aria-label="Close" size="small">
              <CloseIcon />
            </IconButton>
          </div>

          <div className="flex-grow overflow-y-auto overflow-x-hidden bg-mm-neutral-2-50">
            <SprintSettingsStep
              sprintDto={sprintDto}
              setSprintDto={setSprintDto}
              isEdit={editing}
            />
          </div>

          {/* Sticky rather than at the end of the content: the form scrolls past a
              two-month calendar, so the save used to sit below everything it saves. */}
          <div className="flex items-center justify-end gap-3 border-t border-mm-neutral-1-200 bg-white px-6 py-3">
            <Button onClick={() => setEditing(false)} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                updateSprint(sprintDto)
                setEditing(false)
              }}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Drawer>

      <ConfirmationModal
        openState={[isConfirmDeleteOpen, setIsConfirmDeleteOpen]}
        onConfirm={deleteSprint}
        confirmButtonText="Delete Sprint"
        confirmButtonType="delete"
        warningText="All Creatives and Published Campaigns will not be deleted, but their association with this sprint will be removed."
      />

      <FullPageContent className="flex overflow-x-hidden">
        {sprints?.length === 0 && (
          <div className="mx-auto">
            <NoItemsCreated
              title="No sprints yet"
              subtitle="Start your first sprint from the button above."
            />
          </div>
        )}

        {sprintsLoading && (
          <div className="w-full justify-center p-4">
            <Loading />
          </div>
        )}

        {selectedSprint && selectedSprintId && (
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <SprintContent
              key={selectedSprintId}
              selectedSprintId={selectedSprintId}
              tab={tab}
              navigation={
                <Tabs
                  value={tab}
                  onChange={(_, value: SprintTab) => changeTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="Sprint sections"
                  className="min-w-0 max-w-full flex-shrink-0"
                  sx={{ minHeight: 44 }}
                >
                  {tabs.map(({ id, label }) => (
                    <Tab
                      key={id}
                      value={id}
                      label={label}
                      id={`sprint-tab-${id}`}
                      aria-controls={`sprint-panel-${id}`}
                      sx={{ minWidth: 0, minHeight: 44, px: 1.5, textTransform: 'none' }}
                    />
                  ))}
                </Tabs>
              }
            />
          </div>
        )}
      </FullPageContent>
    </UserLayout>
  )
}
